import type { DatasetId, EdgeType, Locale, MythDataset, NodeType, Story } from './data/schema'

export type AppMode = 'explore' | 'node' | 'edge' | 'story'
export type GraphLens = 'event' | 'story' | 'all'
export type StoryView = 'overview' | 'chapter'

export interface UrlState {
  locale: Locale
  datasetId: DatasetId
  mode: AppMode
  focusId?: string
  timelineId?: string
  eraId?: string
  subEraId?: string
  timelineSceneId?: string
  timelineTick?: number
  storyId?: string
  storyView: StoryView
  step: number
  lens: GraphLens
  contextId?: string
  collectionId?: string
  collectionVariantId?: string
}

const modes = new Set<AppMode>(['explore', 'node', 'edge', 'story'])
const lenses = new Set<GraphLens>(['event', 'story', 'all'])
const datasetIdPattern = /^[a-z0-9-]+$/
const localePathAliases: Record<string, Locale> = {
  en: 'en',
  'zh-hant': 'zh-Hant',
}

function pathSegments(pathname: string) {
  return pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => {
      try { return decodeURIComponent(segment).toLowerCase() }
      catch { return segment.toLowerCase() }
    })
}

function parsePathState(pathname: string) {
  const segments = pathSegments(pathname)
  const locale = localePathAliases[segments[0] ?? '']
  let cursor = locale ? 1 : 0
  const datasetCandidate = segments[cursor]
  const datasetId = datasetCandidate && datasetIdPattern.test(datasetCandidate) && datasetCandidate !== 'library'
    ? datasetCandidate
    : undefined
  if (datasetId) cursor += 1
  const routeKind = segments[cursor]
  const routeId = segments[cursor + 1]
  if (routeKind === 'stories' && routeId && datasetIdPattern.test(routeId)) {
    return { locale, datasetId, mode: 'story' as const, storyId: routeId }
  }
  if (routeKind === 'atlas' && routeId && datasetIdPattern.test(routeId)) {
    return { locale, datasetId, mode: 'node' as const, focusId: routeId }
  }
  return { locale, datasetId }
}

export function resolveLocalePreference(search: string, storedLocale?: string | null, pathname = ''): Locale | null {
  const urlLocale = new URLSearchParams(search).get('lang')
  if (urlLocale === 'en' || urlLocale === 'zh-Hant') return urlLocale
  const pathLocale = parsePathState(pathname).locale
  if (pathLocale) return pathLocale
  if (storedLocale === 'en' || storedLocale === 'zh-Hant') return storedLocale
  return null
}

export function parseUrlState(search: string, storedLocale?: string | null, pathname = ''): UrlState {
  const params = new URLSearchParams(search)
  const pathState = parsePathState(pathname)
  const locale = resolveLocalePreference(search, storedLocale, pathname) ?? 'en'
  const modeParam = params.get('mode') as AppMode | null
  const mode = modeParam && modes.has(modeParam) ? modeParam : pathState.mode ?? 'explore'
  const datasetParam = params.get('dataset')
  const datasetId = datasetParam && datasetIdPattern.test(datasetParam) ? datasetParam : pathState.datasetId ?? 'greek'
  const focusId = params.get('focus') ?? pathState.focusId ?? undefined
  const lensParam = params.get('lens') as GraphLens | null
  const requestedLens = lensParam && lenses.has(lensParam) ? lensParam : undefined
  const storyView: StoryView = mode === 'story' && params.get('view') === 'overview' ? 'overview' : 'chapter'
  const parsedStep = Number(params.get('step') ?? '1')
  const parsedTimelineTick = Number(params.get('tick') ?? '0')
  const defaultStoryLens: GraphLens = storyView === 'overview' ? 'story' : 'event'
  const storyLens = storyView === 'overview' && requestedLens === 'event' ? 'story' : requestedLens ?? defaultStoryLens
  const collectionId = mode === 'explore' || mode === 'node' || mode === 'edge' ? params.get('collection') ?? undefined : undefined
  return {
    locale,
    datasetId,
    mode,
    focusId,
    timelineId: mode === 'explore' ? params.get('timeline') ?? undefined : undefined,
    eraId: mode === 'explore' ? params.get('era') ?? undefined : undefined,
    subEraId: mode === 'explore' ? params.get('subera') ?? undefined : undefined,
    timelineSceneId: mode === 'explore' ? params.get('scene') ?? undefined : undefined,
    timelineTick: Number.isInteger(parsedTimelineTick) && parsedTimelineTick >= 0 ? parsedTimelineTick : 0,
    storyId: params.get('story') ?? pathState.storyId ?? undefined,
    storyView,
    step: Number.isInteger(parsedStep) && parsedStep > 0 ? parsedStep : 1,
    lens: mode === 'story' && focusId ? storyLens : mode === 'story' ? defaultStoryLens : 'all',
    contextId: params.get('context') ?? undefined,
    collectionId,
    collectionVariantId: collectionId ? params.get('collectionVariant') ?? undefined : undefined,
  }
}

export function serializeUrlState(state: UrlState): string {
  const params = new URLSearchParams()
  params.set('lang', state.locale)
  if (state.datasetId !== 'greek') params.set('dataset', state.datasetId)
  if (state.mode !== 'explore' || state.timelineId) params.set('mode', state.mode)
  if (state.focusId) params.set('focus', state.focusId)
  if (state.mode === 'explore' && state.timelineId) {
    params.set('timeline', state.timelineId)
    if (state.eraId) params.set('era', state.eraId)
    if (state.subEraId) params.set('subera', state.subEraId)
    if (state.timelineSceneId) params.set('scene', state.timelineSceneId)
    else params.set('tick', String(Math.max(0, state.timelineTick ?? 0)))
  }
  if (state.storyId) params.set('story', state.storyId)
  if (state.mode === 'story' && state.storyView === 'overview') params.set('view', 'overview')
  if (state.mode === 'story' && state.storyView === 'chapter' && state.step > 1) params.set('step', String(state.step))
  const defaultLens: GraphLens = state.mode === 'story' && state.storyView === 'overview' ? 'story' : state.mode === 'story' ? 'event' : 'all'
  const serializableLens = state.mode === 'story' && state.focusId ? state.lens : defaultLens
  if (serializableLens !== defaultLens) params.set('lens', serializableLens)
  if (state.contextId) params.set('context', state.contextId)
  if (state.collectionId && state.mode !== 'story') {
    params.set('collection', state.collectionId)
    if (state.collectionVariantId) params.set('collectionVariant', state.collectionVariantId)
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

interface NeighborhoodContext {
  storyId?: string
  stepId?: string
}

export const NEIGHBOR_EXPANSION_BATCH = 8

export function getStoryOverviewGraph(dataset: MythDataset, story: Story) {
  const nodeIds = new Set(story.steps.flatMap((step) => step.nodeIds))
  const explicitEdgeIds = new Set(story.steps.flatMap((step) => step.edgeIds))
  const belongsToStory = (edge: MythDataset['edges'][number]) => edge.storyRefs.includes(story.id)
    || edge.contexts.some((context) => context.storyIds.includes(story.id))
  const edges = dataset.edges.filter((edge) => nodeIds.has(edge.source)
    && nodeIds.has(edge.target)
    && (explicitEdgeIds.has(edge.id) || belongsToStory(edge)))
  return {
    nodes: dataset.nodes.filter((node) => nodeIds.has(node.id)),
    edges,
  }
}

export function getNeighborhood(dataset: MythDataset, focusId: string, neighborLimit?: number, context: NeighborhoodContext = {}) {
  const belongsToContext = (edge: MythDataset['edges'][number]) => !context.storyId
    || edge.storyRefs.includes(context.storyId)
    || edge.contexts.some((item) => item.storyIds.includes(context.storyId!))
  const touching = dataset.edges.filter((edge) => (edge.source === focusId || edge.target === focusId) && belongsToContext(edge))
  const score: Record<EdgeType, number> = {
    family: 9, causality: 8, conflict: 7, help: 6, love: 5, creation: 4, possession: 3, participation: 2, geography: 1,
  }
  const relevance = (edge: MythDataset['edges'][number]) => {
    const inStep = Boolean(context.stepId && edge.contexts.some((item) => item.stepIds.includes(context.stepId!)))
    const inStory = Boolean(context.storyId && (edge.storyRefs.includes(context.storyId) || edge.contexts.some((item) => item.storyIds.includes(context.storyId!))))
    return (inStep ? 1000 : 0) + (inStory ? 500 : 0) + score[edge.type] * 10 + (edge.featured ? 5 : 0)
  }
  const rankedAll = [...touching].sort((a, b) => relevance(b) - relevance(a) || a.id.localeCompare(b.id))
  const rankedNeighborIds = [...new Set(rankedAll.map((edge) => edge.source === focusId ? edge.target : edge.source))]
  const selectedNeighborIds = new Set(neighborLimit === undefined ? rankedNeighborIds : rankedNeighborIds.slice(0, Math.max(0, neighborLimit)))
  const ranked = rankedAll.filter((edge) => selectedNeighborIds.has(edge.source === focusId ? edge.target : edge.source))
  const ids = new Set([focusId])
  ranked.forEach((edge) => { ids.add(edge.source); ids.add(edge.target) })
  const primaryEdgeIds = new Set(ranked.map((edge) => edge.id))
  const secondary = dataset.edges
    .filter((edge) => belongsToContext(edge) && ids.has(edge.source) && ids.has(edge.target) && !primaryEdgeIds.has(edge.id))
    .sort((a, b) => a.id.localeCompare(b.id))
  return {
    nodes: dataset.nodes.filter((node) => ids.has(node.id)),
    edges: [...ranked, ...secondary],
    overflow: rankedAll.filter((edge) => !selectedNeighborIds.has(edge.source === focusId ? edge.target : edge.source)),
  }
}

export function filterGraph(
  dataset: Pick<MythDataset, 'nodes' | 'edges'>,
  nodeTypes: ReadonlySet<NodeType>,
  preservedNodeIds: ReadonlySet<string> = new Set(),
) {
  const nodes = dataset.nodes.filter((node) => nodeTypes.has(node.type) || preservedNodeIds.has(node.id))
  const ids = new Set(nodes.map((node) => node.id))
  return { nodes, edges: dataset.edges.filter((edge) => ids.has(edge.source) && ids.has(edge.target)) }
}
